import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Pencil, Plus, Warehouse as WarehouseIcon } from 'lucide-react';

import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import Loader from '../../../shared/components/ui/Loader';
import Modal from '../../../shared/components/ui/Modal';
import { StatusPill } from '../../../shared/components/ui/primitives';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useProjectsList } from '../../../../contexts/ProjectsContext';
import { useSmartBack, shouldShowModuleHomeBack } from '../../../shared/navigation';
import {
  createWarehouse,
  makeDefaultWarehouse,
  setWarehouseStatus,
  updateWarehouse
} from '../../services/api/implementation/impl';
import {
  getSavedInventoryProjectId,
  saveInventoryProjectId,
  useProjectWarehouses
} from '../../hooks/useInventorySelection';

const errorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

const formatMoney = (value) => `₹${Math.round(Number(value) || 0).toLocaleString('en-IN')}`;

const Warehouses = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { collapsed } = useSidebar();
  const { onBack } = useSmartBack({ fallback: '/builder/inventory' });
  const showHomeBack = shouldShowModuleHomeBack('inventory', location);
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const queryClient = useQueryClient();

  const [projectId, setProjectId] = useState(
    () => Number(location.state?.projectID) || getSavedInventoryProjectId() || ''
  );
  // { id?, name, address } while the add / edit form is open
  const [form, setForm] = useState(null);
  // { warehouse, action: 'default' | 'deactivate' | 'activate' } while confirming
  const [confirm, setConfirm] = useState(null);

  useEffect(() => {
    if (!projects?.length) return;
    if (!projectId || !projects.some((p) => Number(p.id) === Number(projectId))) {
      setProjectId(getSavedInventoryProjectId(projects) || projects[0].id);
    }
  }, [projectId, projects]);

  useEffect(() => {
    if (projectId) saveInventoryProjectId(projectId);
  }, [projectId]);

  const { warehouses, isFetching, error } = useProjectWarehouses(projectId);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['projectWarehouses'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const name = form.name.trim();
      if (!name) throw new Error('Enter a name');
      const address = form.address.trim();
      return form.id
        ? updateWarehouse(form.id, { name, address })
        : createWarehouse({ project_id: Number(projectId), name, ...(address && { address }) });
    },
    onSuccess: () => {
      toast.success(form?.id ? 'Warehouse updated' : 'Warehouse added');
      setForm(null);
      refresh();
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not save the warehouse'))
  });

  const actionMutation = useMutation({
    mutationFn: ({ warehouse, action }) =>
      action === 'default'
        ? makeDefaultWarehouse(warehouse.id)
        : setWarehouseStatus(warehouse.id, action === 'activate'),
    onSuccess: (_data, { warehouse, action }) => {
      toast.success(
        action === 'default'
          ? `${warehouse.name} is now the default`
          : `${warehouse.name} ${action === 'activate' ? 'activated' : 'deactivated'}`
      );
      setConfirm(null);
      refresh();
    },
    onError: (err) => toast.error(errorMessage(err, 'Could not update the warehouse'))
  });

  const confirmText = confirm
    ? {
        default: {
          title: 'Make default?',
          body: `${confirm.warehouse.name} becomes the project's main warehouse. New POs and releases pick it first. The current default becomes a normal warehouse.`,
          button: 'Make default'
        },
        deactivate: {
          title: 'Deactivate warehouse?',
          body: `${confirm.warehouse.name} can't be picked for new stock, POs or transfers until it is activated again.`,
          button: 'Deactivate'
        },
        activate: {
          title: 'Activate warehouse?',
          body: `${confirm.warehouse.name} can be used again.`,
          button: 'Activate'
        }
      }[confirm.action]
    : null;

  return (
    <Layout>
      <Loader isVisible={isProjectsLoading || isFetching} />
      <div
        className={`fixed top-0 z-10 bg-white ${
          collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
        }`}>
        <Header
          data={{ name: 'Warehouses' }}
          backArrow={showHomeBack}
          onBack={showHomeBack ? onBack : undefined}
        />
      </div>

      <div className="px-6 pb-8 pt-[5.5rem]">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-[#1E4A53]">Where the project keeps stock</h2>
            <p className="text-sm text-gray-500">
              The default warehouse is used whenever nobody picks one.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={projectId || ''}
              onChange={(event) => setProjectId(Number(event.target.value))}
              className="h-10 min-w-[12rem] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-800">
              {(projectsForDropdown || []).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.label || project.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() =>
                navigate('/builder/inventory/transfers', {
                  state: { projectID: Number(projectId), openForm: true }
                })
              }
              className="h-10 rounded-lg border border-[#2A565E] px-4 text-sm font-semibold text-[#2A565E]">
              Transfer stock
            </button>
            <button
              type="button"
              disabled={!projectId}
              onClick={() => setForm({ name: '', address: '' })}
              className="flex h-10 items-center gap-2 rounded-lg bg-[#2A565E] px-4 text-sm font-semibold text-white disabled:opacity-60">
              <Plus size={16} />
              Add warehouse
            </button>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
          <div className="flex items-center gap-2 border-b border-gray-100 px-4 py-3">
            <WarehouseIcon size={18} className="text-[#2A565E]" />
            <h3 className="text-sm font-semibold text-[#1E4A53]">
              Warehouses ({warehouses.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#F4F7F8] text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Address</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Items</th>
                  <th className="px-4 py-3">Stock value</th>
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {error ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-red-600">
                      {errorMessage(error, 'Could not load warehouses')}
                    </td>
                  </tr>
                ) : warehouses.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-gray-400">
                      {isFetching ? 'Loading…' : 'No warehouses for this project'}
                    </td>
                  </tr>
                ) : (
                  warehouses.map((warehouse) => {
                    const active = warehouse.is_active !== false;
                    return (
                      <tr
                        key={warehouse.id}
                        className={`border-t border-gray-100 ${active ? '' : 'opacity-60'}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{warehouse.name}</span>
                            {warehouse.is_default && (
                              <span className="rounded-full bg-[#E9F5F6] px-2 py-0.5 text-[11px] font-semibold text-[#2A565E]">
                                Default
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{warehouse.address || '—'}</td>
                        <td className="px-4 py-3">
                          <StatusPill dense tone={active ? 'success' : 'neutral'}>
                            {active ? 'Active' : 'Inactive'}
                          </StatusPill>
                        </td>
                        <td className="px-4 py-3">{Number(warehouse.sku_count) || 0}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {formatMoney(warehouse.stock_value)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              title="Edit"
                              onClick={() =>
                                setForm({
                                  id: warehouse.id,
                                  name: warehouse.name || '',
                                  address: warehouse.address || ''
                                })
                              }
                              className="rounded-lg border border-gray-200 p-1.5 text-gray-600 hover:text-[#2A565E]">
                              <Pencil size={14} />
                            </button>
                            {!warehouse.is_default && active && (
                              <button
                                type="button"
                                onClick={() => setConfirm({ warehouse, action: 'default' })}
                                className="rounded-lg border border-[#2A565E] px-3 py-1 text-xs font-semibold text-[#2A565E]">
                                Make default
                              </button>
                            )}
                            {!warehouse.is_default && (
                              <button
                                type="button"
                                onClick={() =>
                                  setConfirm({
                                    warehouse,
                                    action: active ? 'deactivate' : 'activate'
                                  })
                                }
                                className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-semibold text-gray-700">
                                {active ? 'Deactivate' : 'Activate'}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {form && (
        <Modal
          name={form.id ? 'Edit warehouse' : 'Add warehouse'}
          width={35}
          onClose={() => setForm(null)}>
          <div className="space-y-4">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Name *</span>
              <input
                autoFocus
                maxLength={150}
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Yard 2"
                className="h-10 w-full rounded-lg border border-gray-200 px-3 outline-none focus:border-[#2A565E]"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-gray-700">Address</span>
              <textarea
                rows={3}
                maxLength={500}
                value={form.address}
                onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                placeholder="Where vendors deliver"
                className="w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-[#2A565E]"
              />
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setForm(null)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700">
                Cancel
              </button>
              <button
                type="button"
                disabled={saveMutation.isPending}
                onClick={() => saveMutation.mutate()}
                className="rounded-lg bg-[#2A565E] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {saveMutation.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {confirm && confirmText && (
        <Modal name={confirmText.title} width={35} onClose={() => setConfirm(null)}>
          <p className="text-sm text-gray-700">{confirmText.body}</p>
          <div className="mt-4 flex justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700">
              Cancel
            </button>
            <button
              type="button"
              disabled={actionMutation.isPending}
              onClick={() => actionMutation.mutate(confirm)}
              className="rounded-md bg-[#2a565e] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {actionMutation.isPending ? 'Saving…' : confirmText.button}
            </button>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default Warehouses;
